import {
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { FormDialog } from '../../../shared/components/FormDialog';
import { DatePickerPtBrField } from '../../../shared/components/DatePickerPtBr';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { employeeTaxaInputSchema, type EmployeeTaxaFormValues } from './schemas';
import type { EmployeeTaxaDto, EmployeeTaxaInput } from './types';

export interface EmployeeTaxaFormDialogProps {
  open: boolean;
  taxa: EmployeeTaxaDto | null;
  hooks: {
    useCreate: () => UseMutationResult<EmployeeTaxaDto, Error, EmployeeTaxaInput>;
    useUpdate: () => UseMutationResult<
      EmployeeTaxaDto,
      Error,
      { id: string; input: EmployeeTaxaInput }
    >;
  };
  onClose: () => void;
}

const DEFAULT_VALUES: EmployeeTaxaFormValues = {
  dataVigencia: new Date().toISOString().slice(0, 10),
  tipoPagamento: 'H',
  valorHoraPagar: 0,
  valorHoraReceber: 0,
  valorFixoPagar: 0,
  valorFixoReceber: 0,
};

/** Converte número do form para string decimal com 2 casas (padrão GeneXus). */
function toMoneyStr(n: number): string {
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
}

export function EmployeeTaxaFormDialog({
  open,
  taxa,
  hooks,
  onClose,
}: EmployeeTaxaFormDialogProps): JSX.Element {
  const { t } = useTranslation();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    EmployeeTaxaFormValues,
    EmployeeTaxaDto,
    EmployeeTaxaInput,
    string
  >({
    open,
    entity: taxa,
    schema: employeeTaxaInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (tx) => ({
      dataVigencia: tx.dataVigencia,
      tipoPagamento: (tx.tipoPagamento ?? 'H') as 'H' | 'F',
      valorHoraPagar: Number(tx.valorHoraPagar) || 0,
      valorHoraReceber: Number(tx.valorHoraReceber) || 0,
      valorFixoPagar: Number(tx.valorFixoPagar) || 0,
      valorFixoReceber: Number(tx.valorFixoReceber) || 0,
    }),
    toInput: (v) => ({
      dataVigencia: v.dataVigencia,
      tipoPagamento: v.tipoPagamento,
      valorHoraPagar: toMoneyStr(v.valorHoraPagar),
      valorHoraReceber: toMoneyStr(v.valorHoraReceber),
      valorFixoPagar: toMoneyStr(v.valorFixoPagar),
      valorFixoReceber: toMoneyStr(v.valorFixoReceber),
    }),
    getId: (tx) => tx.dataVigencia,
    hooks: { useCreate: hooks.useCreate, useUpdate: hooks.useUpdate },
    messages: {
      created: t('features.colaboradores.taxas.toasts.created'),
      updated: t('features.colaboradores.taxas.toasts.updated'),
    },
    focusField: undefined,
    onClose,
  });

  // Observar tipoPagamento p/ habilitar/desabilitar campos fixo.
  const tipoPagamento = useWatch({ control: form.control, name: 'tipoPagamento' });
  const isFixo = tipoPagamento === 'F';

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.colaboradores.taxas.dialog.editTitle')
          : t('features.colaboradores.taxas.dialog.newTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="md"
    >
      <Stack spacing={2}>
        {/* dataVigencia: imutável em edit (PK). */}
        <DatePickerPtBrField
          control={form.control}
          name="dataVigencia"
          label={t('features.colaboradores.taxas.fields.dataVigencia')}
          disabled={isEdit}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: isEdit
                ? t('features.colaboradores.taxas.fields.dataVigenciaLockedHelper')
                : t('features.colaboradores.taxas.fields.dataVigenciaHelper'),
            },
          }}
        />

        {/* tipoPagamento: H ou F. */}
        <Controller
          control={form.control}
          name="tipoPagamento"
          render={({ field }) => (
            <FormControl>
              <FormLabel id="taxa-tipo-pagamento-label">
                {t('features.colaboradores.taxas.fields.tipoPagamento')}
              </FormLabel>
              <RadioGroup row aria-labelledby="taxa-tipo-pagamento-label" {...field}>
                <FormControlLabel
                  value="H"
                  control={<Radio />}
                  label={t('features.colaboradores.taxas.tipoPagamento.H')}
                />
                <FormControlLabel
                  value="F"
                  control={<Radio />}
                  label={t('features.colaboradores.taxas.tipoPagamento.F')}
                />
              </RadioGroup>
            </FormControl>
          )}
        />

        {/* valorHora* sempre obrigatórios. */}
        <Typography variant="caption" color="text.secondary">
          {t('features.colaboradores.taxas.sections.valoresHora')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('features.colaboradores.taxas.fields.valorHoraPagar')}
            type="number"
            fullWidth
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            error={Boolean(form.formState.errors.valorHoraPagar)}
            helperText={form.formState.errors.valorHoraPagar?.message}
            {...form.register('valorHoraPagar', { valueAsNumber: true })}
          />
          <TextField
            label={t('features.colaboradores.taxas.fields.valorHoraReceber')}
            type="number"
            fullWidth
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            error={Boolean(form.formState.errors.valorHoraReceber)}
            helperText={form.formState.errors.valorHoraReceber?.message}
            {...form.register('valorHoraReceber', { valueAsNumber: true })}
          />
        </Stack>

        {/* valorFixo* — obrigatório quando F; em H, fica desabilitado e fixado em 0. */}
        <Typography variant="caption" color="text.secondary">
          {t('features.colaboradores.taxas.sections.valoresFixo')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('features.colaboradores.taxas.fields.valorFixoPagar')}
            type="number"
            fullWidth
            disabled={!isFixo}
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            error={Boolean(form.formState.errors.valorFixoPagar)}
            helperText={
              form.formState.errors.valorFixoPagar?.message ??
              (isFixo
                ? t('features.colaboradores.taxas.fields.valorFixoRequired')
                : t('features.colaboradores.taxas.fields.valorFixoOnlyInF'))
            }
            {...form.register('valorFixoPagar', { valueAsNumber: true })}
          />
          <TextField
            label={t('features.colaboradores.taxas.fields.valorFixoReceber')}
            type="number"
            fullWidth
            disabled={!isFixo}
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            error={Boolean(form.formState.errors.valorFixoReceber)}
            helperText={
              form.formState.errors.valorFixoReceber?.message ??
              (isFixo
                ? t('features.colaboradores.taxas.fields.valorFixoRequired')
                : t('features.colaboradores.taxas.fields.valorFixoOnlyInF'))
            }
            {...form.register('valorFixoReceber', { valueAsNumber: true })}
          />
        </Stack>
      </Stack>
    </FormDialog>
  );
}
